import type { RhythmConfig } from '@/lib/exercise/rhythmEngine';

export type LessonStatus = 'ready' | 'development';

export type LessonMode =
  /** reconhecer a nota e tocar a tecla certa */
  | 'note'
  /** a nota está em uma linha ou em um espaço? */
  | 'line-space'
  /** perguntas de múltipla escolha sobre a clave de sol */
  | 'clef-quiz'
  /** exercício rítmico com metrônomo (pulso, duração e melodia) */
  | 'rhythm'
  /** apenas apresentação, sem exercício ainda */
  | 'concept';

export interface Lesson {
  id: number;
  moduleId: number;
  title: string;
  objective: string;
  /** explicação curta mostrada antes do exercício */
  explanation: string;
  status: LessonStatus;
  mode: LessonMode;
  /** notas em jogo nas aulas de reconhecimento */
  notes?: string[];
  /** número de exercícios sugeridos por sessão (aulas de nota) */
  goal?: number;
  /** dica de apoio exibida abaixo do pentagrama */
  hint?: string;
  /** conjunto de perguntas quando o modo é 'clef-quiz' */
  quiz?: 'clef' | 'signs' | 'accidentals' | 'bass-clef';
  /** receita do exercício rítmico (módulos de ritmo) */
  rhythm?: RhythmConfig;
}

export interface CourseModule {
  id: number;
  title: string;
  summary: string;
  lessonIds: number[];
}

const READY_GOAL = 8;

export const MODULES: CourseModule[] = [
  {
    id: 1,
    title: 'Domínio das notas na clave de sol',
    summary: 'Região central, notas agudas, notas graves, leitura completa e simbologia essencial.',
    lessonIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },
  {
    id: 2,
    title: 'Ler no pulso',
    summary: 'Pulsação constante, uma nota por pulso, compasso 4/4 e a primeira melodia.',
    lessonIds: [11, 12, 13, 14, 15, 16],
  },
  {
    id: 3,
    title: 'Duração das notas',
    summary: 'Semínima, mínima, semibreve e a chegada das colcheias.',
    lessonIds: [17, 18, 19, 20, 21, 22, 23, 24],
  },
  {
    id: 4,
    title: 'Pausas e fluência',
    summary: 'Pausas, leitura antecipada e as primeiras melodias completas.',
    lessonIds: [25, 26, 27, 28, 29, 30],
  },
  {
    id: 5,
    title: 'Ampliação das notas',
    summary: 'Notas acima e abaixo da região inicial e pequenos saltos.',
    lessonIds: [31, 32, 33, 34, 35, 36],
  },
  {
    id: 6,
    title: 'Ritmos mais elaborados',
    summary: 'Semicolcheias, ponto de aumento, ligaduras e ritmos mistos.',
    lessonIds: [37, 38, 39, 40, 41],
  },
  {
    id: 7,
    title: 'Compassos e sinais',
    summary: 'Compassos 2/4, 3/4, 4/4 e sinais de repetição e dinâmica.',
    lessonIds: [42, 43, 44, 45],
  },
  {
    id: 8,
    title: 'Revisão e música final',
    summary: 'Revisar o essencial, ensaiar e terminar tocando uma partitura simples do começo ao fim.',
    lessonIds: [46, 47, 48],
  },
];

const ALL_SEVEN = ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4'];
const REGION_LOW = ['B3', 'A3', 'G3'];
const REGION_HIGH = ['C5', 'D5', 'E5'];

export const LESSONS: Lesson[] = [
  // ── Módulo 1 — Encontrar as notas ───────────────────────────────────────────
  {
    id: 1,
    moduleId: 1,
    title: 'Clave de sol e região central',
    objective: 'Entender a clave de sol e dominar Dó4 a Si4 por posição, som e teclado.',
    explanation:
      'Primeiro você entende as cinco linhas e a referência da clave de sol. Depois observa, memoriza, toca sequências, treina linhas, espaços, mistura tudo e aumenta a velocidade até 98% de domínio.',
    status: 'ready',
    mode: 'note',
    notes: ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4'],
    goal: 50,
    hint: 'Não pule etapas: a meta é reconhecer a posição praticamente ao olhar.',
  },
  {
    id: 2,
    moduleId: 1,
    title: 'Região acima do Si',
    objective: 'Aplicar o mesmo método de domínio às notas Dó5 a Si5.',
    explanation:
      'Depois de dominar Dó4 a Si4, a sequência continua acima do Si. Você repete o mesmo caminho: observar, memorizar, tocar em ordem, separar linhas e espaços, misturar e acelerar.',
    status: 'ready',
    mode: 'note',
    notes: ['C5', 'D5', 'E5', 'F5', 'G5', 'A5', 'B5'],
    goal: 50,
    hint: 'A sequência de nomes se repete; o que muda é a posição na pauta e a oitava.',
  },
  {
    id: 3,
    moduleId: 1,
    title: 'Região abaixo do Dó central',
    objective: 'Dominar Dó3 a Si3 e as linhas suplementares abaixo da pauta.',
    explanation:
      'Agora a leitura desce. O sistema aplica a mesma técnica para que as linhas suplementares deixem de parecer algo novo ou difícil.',
    status: 'ready',
    mode: 'note',
    notes: ['C3', 'D3', 'E3', 'F3', 'G3', 'A3', 'B3'],
    goal: 50,
    hint: 'Continue pensando em linha e espaço; a pauta apenas foi estendida para baixo.',
  },
  {
    id: 4,
    moduleId: 1,
    title: 'Desafio de leitura completa',
    objective: 'Misturar as regiões grave, central e aguda até o reconhecimento ficar automático.',
    explanation:
      'As notas passam a aparecer em toda a região Dó3 a Si5. O objetivo é olhar a posição e tocar a tecla correta sem contar as linhas uma a uma.',
    status: 'ready',
    mode: 'note',
    notes: ['C3', 'D3', 'E3', 'F3', 'G3', 'A3', 'B3', 'C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'C5', 'D5', 'E5', 'F5', 'G5', 'A5', 'B5'],
    goal: 50,
    hint: 'Nesta fase a resposta deve começar a ser imediata.',
  },
  {
    id: 5,
    moduleId: 1,
    title: 'Simbologia essencial',
    objective: 'Reconhecer os principais símbolos que o iniciante encontra em uma partitura.',
    explanation:
      'Com as notas dominadas, entramos nos sinais essenciais: repetição, dinâmica, andamento e outros símbolos básicos que orientam a execução.',
    status: 'ready',
    mode: 'clef-quiz',
    quiz: 'signs',
    goal: 4,
    hint: 'Leia o símbolo e pense no que ele pede ao músico antes de responder.',
  },
  {
    id: 6,
    moduleId: 1,
    title: 'Dó4 a Mi4',
    objective: 'Acrescentar Mi4, a primeira linha da pauta.',
    explanation:
      'O Mi4 fica sobre a primeira linha de baixo. Agora você tem três notas: Dó4, Ré4 e Mi4.',
    status: 'ready',
    mode: 'note',
    notes: ['C4', 'D4', 'E4'],
    goal: READY_GOAL,
    hint: 'Se a nota está em cima da primeira linha, é Mi4.',
  },
  {
    id: 7,
    moduleId: 1,
    title: 'Acrescentar Fá4',
    objective: 'Reconhecer Fá4, no primeiro espaço da pauta.',
    explanation:
      'O Fá4 fica no primeiro espaço, entre a primeira e a segunda linha. Quatro notas em jogo agora.',
    status: 'ready',
    mode: 'note',
    notes: ['C4', 'D4', 'E4', 'F4'],
    goal: READY_GOAL,
    hint: 'Espaço entre as duas linhas de baixo é Fá4.',
  },
  {
    id: 8,
    moduleId: 1,
    title: 'Acrescentar Sol4',
    objective: 'Reconhecer Sol4, a linha da clave de sol.',
    explanation:
      'O Sol4 é a segunda linha, a mesma linha em que a clave de sol enrola. Ajuda a lembrar: clave de sol = linha do Sol.',
    status: 'ready',
    mode: 'note',
    notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
    goal: READY_GOAL,
    hint: 'Se a nota está na linha em que a clave enrola, é Sol4.',
  },
  {
    id: 9,
    moduleId: 1,
    title: 'Acrescentar Lá4 e Si4',
    objective: 'Conhecer Lá4 (segundo espaço) e Si4 (terceira linha), uma de cada vez.',
    explanation:
      'Primeiro o Lá4, no segundo espaço (entre a segunda e a terceira linha). Depois o Si4, em cima da terceira linha. Os exercícios alternam essas notas com as anteriores para fixar a diferença.',
    status: 'ready',
    mode: 'note',
    notes: ALL_SEVEN,
    goal: READY_GOAL,
    hint: 'Lá4 fica no espaço; Si4 fica na linha do meio da pauta.',
  },
  {
    id: 10,
    moduleId: 1,
    title: 'Revisão das sete notas',
    objective: 'Ler Dó4, Ré4, Mi4, Fá4, Sol4, Lá4 e Si4 com sorteio equilibrado.',
    explanation:
      'Hora de juntar tudo. As sete notas naturais vão aparecer em ordem imprevisível. Vá com calma: o importante é acertar.',
    status: 'ready',
    mode: 'note',
    notes: ALL_SEVEN,
    goal: 12,
    hint: 'Tente nomear a nota mentalmente antes de tocar.',
  },

  // ── Módulo 2 — Ler no pulso ────────────────────────────────────────────────
  {
    id: 11,
    moduleId: 2,
    title: 'Pulsação constante',
    objective: 'Ouvir e acompanhar uma pulsação regular, com indicação visual e sonora.',
    explanation:
      'Antes de ler notas, o corpo precisa sentir o pulso. Mantenha uma pulsação constante: marque uma vez por pulso, junto com o metrônomo. Use qualquer tecla — o que importa agora é o tempo.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4'],
      figures: ['quarter'],
      beatsPerMeasure: 4,
      bpm: 70,
      bars: 2,
      responseMode: 'pulse',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Conte junto: 1, 2, 3, 4. O primeiro pulso do compasso é o mais forte.',
  },
  {
    id: 12,
    moduleId: 2,
    title: 'Uma nota por pulso',
    objective: 'Tocar uma nota conhecida em cada pulso.',
    explanation:
      'Agora cada pulso recebe uma nota. Leia a nota da pauta e toque exatamente no pulso do metrônomo. Uma semínima vale um pulso.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter'],
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Espere a contagem inicial terminar antes de tocar a primeira nota.',
  },
  {
    id: 13,
    moduleId: 2,
    title: 'Contagem de quatro pulsos',
    objective: 'Contar grupos de quatro pulsos, destacando o primeiro.',
    explanation:
      'Cada grupo de quatro pulsos forma um compasso. O primeiro pulso é sempre o mais acentuado — é ele que organiza a contagem 1, 2, 3, 4.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4'],
      figures: ['quarter'],
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'pulse',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Sinta o retorno ao pulso forte no “1” de cada compasso.',
  },
  {
    id: 14,
    moduleId: 2,
    title: 'Compasso 4/4',
    objective: 'Tocar compassos simples no compasso 4/4.',
    explanation:
      'No compasso 4/4 contamos quatro pulsos de semínima por compasso. Quatro pulsos não significam, necessariamente, quatro notas diferentes — mas nesta aula cada pulso tem uma nota.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter'],
      beatsPerMeasure: 4,
      bpm: 76,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Cada barra vertical na pauta marca o fim de um compasso.',
  },
  {
    id: 15,
    moduleId: 2,
    title: 'Sete notas no pulso',
    objective: 'Ler as sete notas naturais de Dó4 a Si4 mantendo o pulso.',
    explanation:
      'Agora todas as sete notas aparecem em ritmo. Leia uma nota por pulso e toque sem perder a pulsação, mesmo quando a nota muda.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter'],
      beatsPerMeasure: 4,
      bpm: 76,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 5,
      countIn: 4,
    },
    hint: 'Olhe para a próxima nota enquanto toca a atual.',
  },
  {
    id: 16,
    moduleId: 2,
    title: 'Primeira melodia',
    objective: 'Ler e tocar uma melodia curta formada apenas por conteúdo já ensinado.',
    explanation:
      'Chegou a primeira melodia. São pequenas sequências de notas no pulso, usando somente as sete notas naturais que você já conhece. Toque do começo ao fim sem parar.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter'],
      beatsPerMeasure: 4,
      bpm: 80,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 5,
      countIn: 4,
    },
    hint: 'Se errar uma nota, siga em frente e volte ao pulso na próxima.',
  },

  // ── Módulo 3 — Duração das notas ───────────────────────────────────────────
  {
    id: 17,
    moduleId: 3,
    title: 'Semínima',
    objective: 'Entender que a semínima vale um pulso em 4/4.',
    explanation:
      'A semínima é a figura-base: ela vale um pulso. Nesta aula todas as notas são semínimas, então você toca uma nota por pulso.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'G4'],
      figures: ['quarter'],
      beatsPerMeasure: 4,
      bpm: 76,
      bars: 1,
      responseMode: 'on-beat',
      exercises: 5,
      countIn: 4,
    },
    hint: 'Cabeça cheia e haste: essa é a semínima.',
  },
  {
    id: 18,
    moduleId: 3,
    title: 'Mínima',
    objective: 'Reconhecer a mínima e sua duração de dois pulsos.',
    explanation:
      'A mínima vale dois pulsos. Deixe a nota soar por dois cliques do metrônomo antes de tocar a próxima.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'E4', 'G4'],
      figures: ['half'],
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 1,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Cabeça vazia com haste: essa é a mínima. Ela ocupa metade do compasso 4/4.',
  },
  {
    id: 19,
    moduleId: 3,
    title: 'Semibreve',
    objective: 'Reconhecer a semibreve e sua duração de quatro pulsos.',
    explanation:
      'A semibreve vale o compasso inteiro: quatro pulsos em 4/4. Toque a nota no primeiro pulso e deixe-a soar até o fim do compasso.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'G4'],
      figures: ['whole'],
      beatsPerMeasure: 4,
      bpm: 64,
      bars: 1,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Cabeça vazia e sem haste: essa é a semibreve.',
  },
  {
    id: 20,
    moduleId: 3,
    title: 'Mistura das três durações',
    objective: 'Alternar semínima, mínima e semibreve em compassos válidos.',
    explanation:
      'Agora as três durações se misturam. Conte os pulsos de cada figura para manter o compasso certo antes de tocar.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'half', 'whole'],
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Semínima = 1, mínima = 2, semibreve = 4 pulsos.',
  },
  {
    id: 21,
    moduleId: 3,
    title: 'Colcheia',
    objective: 'Reconhecer a colcheia e sua duração de meio pulso.',
    explanation:
      'A colcheia vale metade de um pulso: duas colcheias cabem em um único pulso. Toque as duas dentro do mesmo clique do metrônomo, sem correr.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'G4'],
      figures: ['eighth'],
      beatsPerMeasure: 4,
      bpm: 68,
      bars: 1,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Cabeça cheia, haste e uma bandeirola: essa é a colcheia.',
  },
  {
    id: 22,
    moduleId: 3,
    title: 'Duas colcheias em um pulso',
    objective: 'Tocar duas colcheias dentro de um único pulso de semínima em 4/4.',
    explanation:
      'Duas colcheias ocupam juntas um pulso de semínima — elas são tocadas em sequência dentro do mesmo pulso, e não ao mesmo tempo. Repare que um compasso 4/4 tem quatro pulsos, não necessariamente quatro notas.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4'],
      figures: ['eighth'],
      beatsPerMeasure: 4,
      bpm: 68,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Conte “1 e 2 e 3 e 4 e”: a nota “e” cai entre dois cliques.',
  },
  {
    id: 23,
    moduleId: 3,
    title: 'Semínimas e colcheias',
    objective: 'Alternar semínimas e pares de colcheias no mesmo compasso.',
    explanation:
      'Combinamos notas de um pulso (semínimas) com pares de meio pulso (colcheias). Mantenha a pulsação firme mesmo quando a figura muda.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'eighth'],
      beatsPerMeasure: 4,
      bpm: 70,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Semínima = 1 pulso; duas colcheias = 1 pulso.',
  },
  {
    id: 24,
    moduleId: 3,
    title: 'Melodia com durações diferentes',
    objective: 'Ler uma melodia curta com várias durações.',
    explanation:
      'Juntamos tudo o que aprendemos sobre duração. Leia a melodia, conte os pulsos de cada figura e toque do começo ao fim.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter', 'half', 'eighth', 'whole'],
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 5,
      countIn: 4,
    },
    hint: 'Vá com calma: o importante é respeitar a duração de cada figura.',
  },

  // ── Módulo 4 — Pausas e fluência ───────────────────────────────────────────
  {
    id: 25,
    moduleId: 4,
    title: 'Pausa de semínima',
    objective: 'Reconhecer a pausa de semínima e ficar um pulso em silêncio.',
    explanation:
      'A pausa de semínima dura um pulso, como a semínima — só que no lugar da nota há silêncio. Quando a pauta pede pausa, não toque nada: espere o próximo pulso. Se você tocar em cima da pausa, o exercício avisa.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'G4'],
      figures: ['quarter'],
      rests: ['quarter'],
      restChance: 0.4,
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Pausa = silêncio de um pulso. Mantenha a contagem na cabeça: 1, 2, 3, 4.',
  },
  {
    id: 26,
    moduleId: 4,
    title: 'Pausas de mínima e semibreve',
    objective: 'Reconhecer pausas de dois e de quatro pulsos.',
    explanation:
      'A pausa de mínima vale dois pulsos e a pausa de semibreve vale um compasso inteiro em 4/4 (quatro pulsos). São silêncios mais longos: conte por dentro até o próximo ataque.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'E4', 'G4', 'A4'],
      figures: ['half', 'whole', 'quarter'],
      rests: ['half', 'whole'],
      restChance: 0.4,
      beatsPerMeasure: 4,
      bpm: 66,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Pausa de mínima = 2 pulsos; pausa de semibreve = 4 pulsos.',
  },
  {
    id: 27,
    moduleId: 4,
    title: 'Pausa de colcheia',
    objective: 'Reconhecer a pausa de colcheia, um silêncio de meio pulso.',
    explanation:
      'A pausa de colcheia dura meio pulso. Ela aparece dentro de um pulso que já tem som — por isso é curtinha: você toca e logo fica em silêncio pela metade do pulso.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4'],
      figures: ['quarter', 'eighth'],
      rests: ['eighth'],
      restChance: 0.35,
      restGlow: true,
      beatsPerMeasure: 4,
      bpm: 68,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Conte “1 e 2 e”: a pausa de colcheia ocupa só o “e”. A dica acende na pauta mostrando o silêncio.',
  },
  {
    id: 28,
    moduleId: 4,
    title: 'Notas e pausas',
    objective: 'Alternar notas e pausas mantendo o pulso firme.',
    explanation:
      'Agora notas e pausas se misturam. O desafio não é tocar mais alto: é continuar contando por dentro durante os silêncios e voltar a tocar no tempo certo.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter', 'half', 'eighth'],
      rests: ['quarter', 'half'],
      restChance: 0.3,
      restGlow: true,
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 5,
      countIn: 4,
    },
    hint: 'Durante a pausa, continue contando os pulsos na cabeça.',
  },
  {
    id: 29,
    moduleId: 4,
    title: 'Leitura antecipada',
    objective: 'Ler a próxima nota enquanto ainda toca a atual.',
    explanation:
      'Aqui o olho treina para andar à frente da mão. Antes de tocar uma nota, procure na pauta a figura seguinte. Começamos em andamento calmo para dar tempo de olhar adiante.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter', 'half'],
      rests: ['quarter'],
      restChance: 0.2,
      beatsPerMeasure: 4,
      bpm: 64,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 5,
      countIn: 4,
    },
    hint: 'Enquanto toca, já procure a nota seguinte com os olhos.',
  },
  {
    id: 30,
    moduleId: 4,
    title: 'Três melodias progressivas',
    objective: 'Ler e tocar três melodias curtas que aumentam de dificuldade.',
    explanation:
      'Fechamos o módulo com três pequenas peças em sequência. A primeira usa figuras simples; a segunda acrescenta pausas; a terceira mistura notas, durações e pausas. Toque cada uma do começo ao fim.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter', 'half', 'whole', 'eighth'],
      rests: ['quarter', 'half'],
      restChance: 0.22,
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 3,
      countIn: 4,
    },
    hint: 'Respire no começo de cada peça e siga o pulso até o fim.',
  },

  // ── Módulo 5 — Ampliação das notas ─────────────────────────────────────────
  {
    id: 31,
    moduleId: 5,
    title: 'Dó5 para Mi5',
    objective: 'Ler Dó5, Ré5 e Mi5 nas posições corretas da clave de sol.',
    explanation:
      'Subimos para a região aguda. Na clave de sol: Dó5 fica no terceiro espaço, Ré5 na quarta linha e Mi5 no quarto espaço. Toque a tecla correspondente a cada altura.',
    status: 'ready',
    mode: 'note',
    notes: REGION_HIGH,
    goal: READY_GOAL,
    hint: 'Quanto mais alta a nota na pauta, mais à direita ela está no teclado.',
  },
  {
    id: 32,
    moduleId: 5,
    title: 'Si3, Lá3 e Sol3',
    objective: 'Ler Si3, Lá3 e Sol3 com as linhas suplementares corretas.',
    explanation:
      'Agora descemos abaixo do Dó central. Si3 fica logo abaixo da linha suplementar do Dó4; Lá3 fica na segunda linha suplementar; e Sol3 fica no espaço logo abaixo dela. Observe as linhas extras desenhadas.',
    status: 'ready',
    mode: 'note',
    notes: REGION_LOW,
    goal: READY_GOAL,
    hint: 'Cada linha suplementar desce meio tom de cada vez. Conte-as com o dedo.',
  },
  { id: 33, moduleId: 5, title: 'Movimentos ascendentes e descendentes', objective: 'Seguir a direção da melodia: quando a nota sobe e quando desce.', explanation: 'A melodia quase nunca fica parada: ela sobe ou desce de uma nota para a vizinha. Antes de tocar, olhe o desenho da pauta — se a cabeça da nota sobe, a melodia sobe; se desce, a melodia desce.', status: 'ready', mode: 'rhythm', rhythm: { notes: ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'C5'], figures: ['quarter'], beatsPerMeasure: 4, bpm: 76, bars: 2, responseMode: 'on-beat', exercises: 4, countIn: 4, motion: 'stepwise' }, hint: 'Acompanhe a linha: subiu na pauta, a tecla vai para a direita; desceu, para a esquerda.' },
  { id: 34, moduleId: 5, title: 'Pequenos saltos', objective: 'Ler saltos de terça e quarta, sem passar pela nota vizinha.', explanation: 'Nem sempre a melodia anda de vizinha em vizinha: às vezes ela salta. Pular a nota do meio é um salto de terça; pular duas é de quarta. Conte as posições na pauta antes de tocar.', status: 'ready', mode: 'rhythm', rhythm: { notes: ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'C5'], figures: ['quarter', 'half'], beatsPerMeasure: 4, bpm: 72, bars: 2, responseMode: 'on-beat', exercises: 4, countIn: 4, motion: 'jumps' }, hint: 'Salto de terça ou quarta: conte duas ou três posições na pauta de uma vez.' },
  { id: 35, moduleId: 5, title: 'Revisão da região ampliada', objective: 'Revisar todas as notas já aprendidas, da região grave à aguda.', explanation: 'Hora de juntar tudo: as sete notas naturais, a região aguda (Dó5 a Mi5) e a região grave (Si3, Lá3 e Sol3) aparecem em ordem imprevisível. Nomeie a nota na cabeça antes de tocar.', status: 'ready', mode: 'rhythm', rhythm: { notes: ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'B3', 'A3', 'G3', 'C5', 'D5', 'E5'], figures: ['quarter', 'half'], beatsPerMeasure: 4, bpm: 72, bars: 2, responseMode: 'on-beat', exercises: 5, countIn: 4 }, hint: 'Observe as linhas suplementares: elas avisam quando a nota saiu da pauta.' },
  { id: 36, moduleId: 5, title: 'Sétima melodia do primeiro ciclo', objective: 'Ler e tocar uma melodia maior, reunindo toda a região ampliada.', explanation: 'Fechamos o módulo com uma melodia mais longa, que mistura notas da região inicial, da aguda e da grave, com semínimas e mínimas. Leia do começo ao fim e mantenha o pulso firme.', status: 'ready', mode: 'rhythm', rhythm: { notes: ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'B3', 'A3', 'G3', 'C5', 'D5', 'E5'], figures: ['quarter', 'half', 'eighth'], beatsPerMeasure: 4, bpm: 74, bars: 3, responseMode: 'on-beat', exercises: 4, countIn: 4 }, hint: 'Se uma nota escapar, siga em frente e retome o pulso na próxima.' },

  // ── Módulo 6 — Ritmos mais elaborados ──────────────────────────────────────
  {
    id: 37,
    moduleId: 6,
    title: 'Semicolcheias',
    objective: 'Reconhecer a semicolcheia e sua duração de um quarto de pulso.',
    explanation:
      'A semicolcheia vale um quarto do pulso: quatro semicolcheias cabem em uma única semínima. É a menor figura do curso — toque cada uma com calma, sem acelerar.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4'],
      figures: ['sixteenth'],
      beatsPerMeasure: 4,
      bpm: 58,
      bars: 1,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Cabeça cheia, haste e duas bandeirolas: essa é a semicolcheia. São quatro por pulso.',
  },
  {
    id: 38,
    moduleId: 6,
    title: 'Colcheias e semicolcheias',
    objective: 'Combinar colcheias (meio pulso) e semicolcheias (um quarto de pulso) mantendo a pulsação.',
    explanation:
      'Agora meio pulso e um quarto de pulso aparecem no mesmo compasso. Conte o pulso por dentro e encaixe as subdivisões sem correr: duas colcheias duram o mesmo que quatro semicolcheias.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['eighth', 'sixteenth'],
      beatsPerMeasure: 4,
      bpm: 56,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Colcheia = meio pulso; semicolcheia = um quarto de pulso. Duas semicolcheias valem uma colcheia.',
  },
  {
    id: 39,
    moduleId: 6,
    title: 'Ponto de aumento',
    objective: 'Entender que o ponto aumenta a figura pela metade da sua duração.',
    explanation:
      'O ponto à direita da figura aumenta a sua duração pela metade. Assim, a mínima pontuada vale três pulsos (dois + um) e a semínima pontuada vale um pulso e meio. Toque a figura pontuada e deixe-a soar por todo o seu tempo.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['dotted-half', 'dotted-quarter', 'quarter', 'eighth'],
      beatsPerMeasure: 4,
      bpm: 64,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Ponto = metade a mais. Mínima (2) vira 3 pulsos; semínima (1) vira 1 pulso e meio.',
  },
  {
    id: 40,
    moduleId: 6,
    title: 'Ligadura de duração',
    objective: 'Somar durações com a ligadura, mantendo o som entre duas notas.',
    explanation:
      'A ligadura é uma curva que une duas notas de mesma altura em um único som: você toca apenas a primeira e segura pela duração das duas juntas. Quando a segunda nota chega, não ataque de novo — continue segurando.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'half'],
      beatsPerMeasure: 4,
      bpm: 64,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
      ties: true,
    },
    hint: 'A curva liga as duas notas: toque uma vez e segure o som até o fim da segunda.',
  },
  {
    id: 41,
    moduleId: 6,
    title: 'Ritmos mistos',
    objective: 'Ler ritmos que combinam semicolcheias, colcheias, semínimas e ponto de aumento.',
    explanation:
      'Fechamos o módulo juntando todo o conteúdo: figuras de um, meio e um quarto de pulso, mais a figura pontuada que ocupa um pulso e meio. Leia contando por dentro e respeite a duração de cada figura.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ALL_SEVEN,
      figures: ['quarter', 'eighth', 'sixteenth', 'dotted-quarter'],
      beatsPerMeasure: 4,
      bpm: 60,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
    },
    hint: 'Vá devagar: o importante é acertar a duração de cada figura, não a velocidade.',
  },

  // ── Módulo 7 — Compassos e sinais ──────────────────────────────────────────
  {
    id: 42,
    moduleId: 7,
    title: 'Compasso 2/4',
    objective: 'Ler e tocar no compasso de dois pulsos por compasso.',
    explanation:
      'No compasso 2/4 cada compasso tem dois pulsos de semínima: o primeiro é forte e o segundo é fraco. É o compasso das marchinhas e de muitas melodias simples. Conte “1, 2” e recomece a cada barra.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter'],
      beatsPerMeasure: 2,
      bpm: 80,
      bars: 4,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 2,
    },
    hint: 'Dois pulsos por compasso: 1 (forte), 2 (fraco). Observe a fórmula 2/4 no início da pauta.',
  },
  {
    id: 43,
    moduleId: 7,
    title: 'Compasso 3/4',
    objective: 'Ler e tocar no compasso de três pulsos, como uma valsa.',
    explanation:
      'No compasso 3/4 cada compasso tem três pulsos de semínima: forte, fraco, fraco. É o compasso da valsa. Conte “1, 2, 3” e sinta o primeiro pulso como apoio.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'half'],
      beatsPerMeasure: 3,
      bpm: 78,
      bars: 4,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 3,
    },
    hint: 'Três pulsos por compasso: 1 (forte), 2 e 3 (fracos).',
  },
  {
    id: 44,
    moduleId: 7,
    title: 'Comparação entre 2/4, 3/4 e 4/4',
    objective: 'Distinguir os compassos de dois, três e quatro pulsos pelo número de pulsos.',
    explanation:
      'As mesmas figuras podem caber em compassos diferentes. Aqui os exercícios alternam 2/4, 3/4 e 4/4: observe a fórmula no início da pauta e conte os pulsos de cada compasso antes de tocar.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'half', 'eighth'],
      beatsPerMeasure: 2,
      timeSignatures: [2, 3, 4],
      bpm: 76,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 6,
    },
    hint: 'A fórmula (2/4, 3/4 ou 4/4) diz quantos pulsos tem cada compasso — conte o primeiro pulso como apoio.',
  },
  {
    id: 45,
    moduleId: 7,
    title: 'Repetição, dinâmica e andamento',
    objective: 'Reconhecer os sinais de repetição e as indicações de dinâmica e de andamento.',
    explanation:
      'Além das notas, a partitura traz sinais que dizem o que repetir, com que força tocar e em que velocidade. As barras de repetição mandam voltar a um trecho; as letras de dinâmica vão de p (suave) a f (forte); e as palavras de andamento, como Allegro, indicam a velocidade.',
    status: 'ready',
    mode: 'clef-quiz',
    quiz: 'signs',
    goal: 4,
    hint: 'piano = suave; forte = forte; Allegro = rápido.',
  },

  // ── Módulo 8 — Revisão e música final ──────────────────────────────────────
  {
    id: 46,
    moduleId: 8,
    title: 'Revisão dos símbolos essenciais',
    objective: 'Confirmar o reconhecimento dos sinais mais importantes para uma leitura inicial.',
    explanation:
      'Antes da música final, revise os símbolos que realmente aparecem em uma partitura simples: clave, barras de compasso, repetição, dinâmica e indicação de andamento.',
    status: 'ready',
    mode: 'clef-quiz',
    quiz: 'signs',
    goal: 4,
    hint: 'Não é preciso decorar teoria avançada: concentre-se no que ajuda a ler e tocar a música.',
  },
  {
    id: 47,
    moduleId: 8,
    title: 'Ensaio para a música final',
    objective: 'Praticar as notas e durações que aparecerão na partitura final.',
    explanation:
      'Este é o ensaio. As combinações mudam, mas usam apenas notas e figuras que você já treinou. Repita até conseguir ler sem precisar parar para pensar em cada símbolo.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'half', 'eighth'],
      beatsPerMeasure: 4,
      bpm: 60,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 8,
      countIn: 4,
    },
    hint: 'Leia um pouco à frente do que está tocando e mantenha o pulso mesmo quando errar.',
  },
  {
    id: 48,
    moduleId: 8,
    title: 'Música final simples',
    objective: 'Tocar uma pequena partitura completa, do início ao fim, com segurança.',
    explanation:
      'Chegou a hora de juntar o essencial. A música final usa poucas notas, compasso 4/4 e figuras básicas. A mesma partitura é repetida para que você perceba sua evolução até tocá-la com domínio.',
    status: 'ready',
    mode: 'rhythm',
    rhythm: {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'half'],
      beatsPerMeasure: 4,
      bpm: 60,
      bars: 4,
      responseMode: 'on-beat',
      exercises: 4,
      countIn: 4,
      fixedSequence: [
        { kind: 'note', noteId: 'C4', durationId: 'quarter' },
        { kind: 'note', noteId: 'D4', durationId: 'quarter' },
        { kind: 'note', noteId: 'E4', durationId: 'quarter' },
        { kind: 'note', noteId: 'C4', durationId: 'quarter' },
        { kind: 'note', noteId: 'E4', durationId: 'quarter' },
        { kind: 'note', noteId: 'F4', durationId: 'quarter' },
        { kind: 'note', noteId: 'G4', durationId: 'half' },
        { kind: 'note', noteId: 'G4', durationId: 'quarter' },
        { kind: 'note', noteId: 'E4', durationId: 'quarter' },
        { kind: 'note', noteId: 'D4', durationId: 'quarter' },
        { kind: 'note', noteId: 'C4', durationId: 'quarter' },
        { kind: 'note', noteId: 'D4', durationId: 'quarter' },
        { kind: 'note', noteId: 'G4', durationId: 'quarter' },
        { kind: 'note', noteId: 'C4', durationId: 'half' },
      ],
    },
    hint: 'É uma pequena música de conclusão: não corra. Mantenha o pulso e leia sempre a próxima nota antes de chegar nela.',
  },
];

export function getLesson(id: number): Lesson | undefined {
  return LESSONS.find((lesson) => lesson.id === id);
}

export function getModule(id: number): CourseModule | undefined {
  return MODULES.find((module) => module.id === id);
}

export function lessonsOfModule(moduleId: number): Lesson[] {
  return LESSONS.filter((lesson) => lesson.moduleId === moduleId);
}

export const READY_LESSON_IDS = LESSONS.filter((lesson) => lesson.status === 'ready').map(
  (lesson) => lesson.id,
);

export const READY_LESSON_COUNT = READY_LESSON_IDS.length;

export function nextLessonId(id: number): number | null {
  return id < LESSONS.length ? id + 1 : null;
}

export function previousLessonId(id: number): number | null {
  return id > 1 ? id - 1 : null;
}